
import React, { useState, useEffect } from 'react';
import { TrackDownload } from '@/lib/types';
import { toast } from "sonner";
import EmptyUploadsState from './uploads/EmptyUploadsState';
import TrackCard from './uploads/track-card';
import DeleteTrackDialog from './uploads/DeleteTrackDialog';

interface UploadsListProps {
  uploads: TrackDownload[];
  onCreateClick: () => void;
  onDeleteTrack: (trackId: string) => Promise<boolean>;
}

const UploadsList: React.FC<UploadsListProps> = ({ uploads, onCreateClick, onDeleteTrack }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [trackToDelete, setTrackToDelete] = useState<string | null>(null);
  const [localUploads, setLocalUploads] = useState<TrackDownload[]>(uploads);

  // Update local state when props change
  useEffect(() => {
    if (uploads && uploads.length > 0) {
      setLocalUploads(uploads);
    }
  }, [uploads]);

  const handleCopyUrl = (id: string) => {
    const url = `${window.location.origin}/download?id=${id}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Rewards page link copied to clipboard");
    });
  };

  const handleDeleteClick = (id: string) => {
    console.log("Delete button clicked for track:", id);
    setTrackToDelete(id);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!trackToDelete) {
      console.error("No track ID set for deletion");
      return;
    }
    
    console.log("Starting deletion process for track:", trackToDelete);
    setIsDeleting(true);
    
    try {
      const success = await onDeleteTrack(trackToDelete);
      
      if (success) {
        console.log("Track deleted successfully:", trackToDelete);
        // Update local state immediately after successful deletion
        setLocalUploads(prev => prev.filter(track => track.id !== trackToDelete));
        toast.success("Track deleted successfully");
      } else {
        console.error("Track deletion failed");
        toast.error("Failed to delete track. Please try again.");
      }
    } catch (error) {
      console.error("Error during track deletion:", error);
      toast.error("An error occurred while deleting the track");
    } finally {
      setIsDeleting(false);
      setDialogOpen(false); // Close the dialog after deletion attempt
      setTrackToDelete(null);
    }
  };

  const handleSaveEdit = async (updatedTrack: TrackDownload) => {
    // Update local state immediately after successful edit
    setLocalUploads(prev => 
      prev.map(track => track.id === updatedTrack.id ? updatedTrack : track)
    );
    
    toast.success("Track updated successfully");
    return Promise.resolve();
  };

  if (localUploads.length === 0) {
    return <EmptyUploadsState onCreateClick={onCreateClick} />;
  }

  return (
    <div className="grid gap-6">
      {localUploads.map((upload) => (
        <TrackCard
          key={upload.id}
          track={upload}
          onCopyUrl={handleCopyUrl}
          onDeleteClick={handleDeleteClick}
          onSaveEdit={handleSaveEdit}
        />
      ))}

      <DeleteTrackDialog
        isOpen={dialogOpen}
        isDeleting={isDeleting}
        onClose={() => {
          console.log("Dialog closed");
          setDialogOpen(false);
          setTrackToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        trackId={trackToDelete || undefined}
      />
    </div>
  );
};

export default UploadsList;
